import React, { useState, useEffect } from "react";
import Select from "react-select";
import Modal from "./elements/Modal";
import { horseService } from "../api";

const customSelectStyles = {
	control: (provided, state) => ({
		...provided,
		backgroundColor: state.isDisabled ? "#e0e0e0" : provided.backgroundColor,
		cursor: state.isDisabled ? "not-allowed" : "default",
	}),
};

const AddHorseModal = ({ isOpen, onClose, onResult }) => {

	const [name, setName] = useState("");
	const [countryOptions, setCountryOptions] = useState([]);
	const [selectedCountry, setSelectedCountry] = useState(null);
	const [year, setYear] = useState("");
	const [selectedSire, setSelectedSire] = useState(null);
	const [selectedDam, setSelectedDam] = useState(null);

	const [sires, setSires] = useState([]);
	const [sireInputValue, setSireInputValue] = useState("");
	const [keywordSire, setKeywordSire] = useState("");

	const [dams, setDams] = useState([]);
	const [damInputValue, setDamInputValue] = useState("");
	const [keywordDam, setKeywordDam] = useState("");

	const [emptyMessage, setEmptyMessage] = useState("Type at least 3 characters");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [loadError, setLoadError] = useState(false);

	const canSubmit = name.trim() !== "" && selectedCountry && 
		(year === "" || (/^\d{4}$/.test(year) && parseInt(year) <= new Date().getFullYear()));

	useEffect(() => {
		if (!isOpen) {
			setName("");
			setSelectedCountry(null);
			setYear("");
			setSelectedSire(null);
			setSelectedDam(null);
			setSires([]);
			setDams([]);
			setSireInputValue("");
			setDamInputValue("");
			setKeywordSire("");
			setKeywordDam("");
			setEmptyMessage("Type at least 3 characters");
		}
	}, [isOpen]);

	useEffect(() => {
		let cancelled = false;

		const fetchCountries = async () => {
			try {
				const response = await horseService.getCountries();
				if (cancelled) return;

				const options = Array.isArray(response)
					? response.map((country) => ({
						value: country,
						label: country,
					}))
					: [];

				setCountryOptions(options);
				setLoadError(false);
			} catch (err) {
				if (!cancelled) {
					console.error("Error fetching countries:", err);
					setLoadError(true);
				}
			}
		};

		fetchCountries();

		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		if (keywordSire.trim() === "") return;

		if (keywordSire.length >= 3) {
			setEmptyMessage("Searching for horses...");
		} else {
			setEmptyMessage("Type at least 3 characters");
		}

		const delayDebounceFn = setTimeout(() => {
			if (keywordSire.length >= 3) {
				horseService
					.find({ keyword: keywordSire, sponsor: false })
					.then((response) => {
						setSires(response);
						if (response.length === 0) {
							setEmptyMessage("No horses found");
						} else {
							setEmptyMessage("Type at least 3 characters");
						}
					})
					.catch((error) => {
						console.error(error);
						setEmptyMessage("Error searching horses");
					});
			}
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [keywordSire]);

	useEffect(() => {
		if (keywordDam.trim() === "") return;

		if (keywordDam.length >= 3) {
			setEmptyMessage("Searching for horses...");
		} else {
			setEmptyMessage("Type at least 3 characters");
		}

		const delayDebounceFn = setTimeout(() => {
			if (keywordDam.length >= 3) {
				horseService
					.find({ keyword: keywordDam, sponsor: false })
					.then((response) => {
						setDams(response);
						if (response.length === 0) {
							setEmptyMessage("No horses found");
						} else {
							setEmptyMessage("Type at least 3 characters");
						}
					})
					.catch((error) => {
						console.error(error);
						setEmptyMessage("Error searching horses");
					});
			}
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [keywordDam]);

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!canSubmit) {
			return;
		}

		setIsSubmitting(true);

		const horseData = {
			name: name.trim(),
			country: selectedCountry?.value || "US",
		};

		if (year) {
			horseData.year = parseInt(year);
		}

		if (selectedSire) {
			horseData.sire_id = selectedSire.value;
		}

		if (selectedDam) {
			horseData.dam_id = selectedDam.value;
		}

		try {
			await horseService.createNewHorse(horseData);
			onClose();
			if (onResult) {
				onResult({ type: 'success' });
			}
		} catch (error) {
			let errorMessage = "An error occurred. Please try again.";

			if (error.message === 'this horse already exists') {
				errorMessage = "This horse already exists. Please try again with a different name.";
			}
			else if (error && typeof error === "object" && error.detail) {
				errorMessage = error.detail;
			} else if (error && typeof error === "object" && error.non_field_errors) {
				errorMessage = error.non_field_errors;
			}
			onClose();
			if (onResult) {
				onResult({ type: 'error', message: errorMessage });
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal isOpen={isOpen} className="Modal__sponsor-container">
			<div className="sponsor-modal">
				<div className="sponsor-modal__header">
					<p className="left">Add New Horse</p>
					<div
						className="sponsor-modal__close"
						onClick={onClose}
					>
						<i className="fa fa-times"></i>
					</div>
				</div>
				<div className="sponsor-modal__content add-horse-form">
					{loadError && (
						<div className="error-message">
							Unexpected error loading countries. Please try reloading
							the page.
						</div>
					)}
					<form onSubmit={handleSubmit}>
						<div className="input-container mb-15">
							<label className="input-field__label is-black mb-5">
								Name <span className="required">*</span>
							</label>
							<input
								name="name"
								type="text"
								className="input-text"
								placeholder="Enter horse name"
								value={name}
								onChange={(e) => setName(e.target.value)}
							/>
						</div>

						<div className="input-container mb-15">
							<label className="input-field__label is-black mb-5">
								Country <span className="required">*</span>
							</label>
							<Select
								options={countryOptions}
								value={selectedCountry}
								onChange={setSelectedCountry}
								className="react-select-container"
								classNamePrefix="react-select"
								placeholder="Select a country"
								isClearable={false}
							/>
						</div>

						<div className="input-container mb-15">
							<label className="input-field__label is-black mb-5">
								Year of birth
							</label>
							<input
								name="year"
								type="text"
								className="input-text"
								placeholder="YYYY"
								value={year}
								onChange={(e) => {
									const val = e.target.value;
									if (val === '' || /^\d{0,4}$/.test(val)) {
										setYear(val);
									}
								}}
								maxLength={4}
							/>
						</div>

						<div className="input-container mb-15">
							<label className="input-field__label is-black mb-5">Sire</label>
							<Select
								options={sires.map((item) => ({
									value: item.id,
									label: item.display_name,
								}))}
								value={selectedSire}
								onChange={(option) => {
									setSelectedSire(option);
									setSireInputValue("");
									setKeywordSire("");
								}}
								inputValue={sireInputValue}
								onInputChange={(inputValue, { action }) => {
									if (action === "input-change" || action === "set-value") {
										setSireInputValue(inputValue);
										setKeywordSire(inputValue);
									}
								}}
								styles={customSelectStyles}
								className="react-select-container"
								classNamePrefix="react-select"
								placeholder="Select a sire"
								noOptionsMessage={() => emptyMessage}
								isClearable
							/>
						</div>

						<div className="input-container mb-15">
							<label className="input-field__label is-black mb-5">Dam</label>
							<Select
								options={dams.map((item) => ({
									value: item.id,
									label: item.display_name,
								}))}
								value={selectedDam}
								onChange={(option) => {
									setSelectedDam(option);
									setDamInputValue("");
									setKeywordDam("");
								}}
								inputValue={damInputValue}
								onInputChange={(inputValue, { action }) => {
									if (action === "input-change" || action === "set-value") {
										setDamInputValue(inputValue);
										setKeywordDam(inputValue);
									}
								}}
								styles={customSelectStyles}
								className="react-select-container"
								classNamePrefix="react-select"
								placeholder="Select a dam"
								noOptionsMessage={() => emptyMessage}
								isClearable
							/>
						</div>

						<div className="add-horse-form__buttons">
							<button
								type="button"
								className="button outline-noradius"
								onClick={onClose}
								disabled={isSubmitting}
							>
								Cancel
							</button>
							<button
								type="submit"
								className="button"
								disabled={!canSubmit || isSubmitting}
							>
								{isSubmitting ? "Adding..." : "Add Horse"}
							</button>
						</div>
					</form>
				</div>
			</div>
		</Modal>
	);
};

export default AddHorseModal;
